import React, { useEffect, useMemo, useState } from 'react';
import './HorizontalScroll.css';

const HorizontalScroll = ({
  children,
  className = '',
  trackClassName = '',
  itemClassName = '',
  ariaLabel = 'Scrollable content',
  itemsPerView = {
    mobile: 1,
    tablet: 2,
    desktop: 3
  },
  advanceBy = 'page'
}) => {
  const childArray = React.Children.toArray(children).filter(Boolean);
  const [startIndex, setStartIndex] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth || 1440);

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth || 1440);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setStartIndex(0);
  }, [childArray.length]);

  const currentItemsPerView = useMemo(() => {
    if (viewportWidth < 768) {
      return itemsPerView.mobile || 1;
    }

    if (viewportWidth < 1200) {
      return itemsPerView.tablet || itemsPerView.mobile || 1;
    }

    return itemsPerView.desktop || itemsPerView.tablet || itemsPerView.mobile || 1;
  }, [itemsPerView.desktop, itemsPerView.mobile, itemsPerView.tablet, viewportWidth]);

  const displayCount = Math.max(1, Math.min(currentItemsPerView, childArray.length || 1));
  const step = advanceBy === 'single' ? 1 : displayCount;

  const visibleItems = useMemo(() => {
    if (childArray.length === 0) {
      return [];
    }

    return Array.from({ length: displayCount }, (_, index) => {
      const childIndex = (startIndex + index) % childArray.length;
      return {
        child: childArray[childIndex],
        key: `${childIndex}-${index}-${startIndex}`
      };
    });
  }, [childArray, displayCount, startIndex]);

  const showControls = childArray.length > 1;

  const handleNext = () => {
    if (!childArray.length) {
      return;
    }

    setStartIndex((currentValue) => (currentValue + step) % childArray.length);
  };

  const handlePrevious = () => {
    if (!childArray.length) {
      return;
    }

    setStartIndex((currentValue) => {
      const nextValue = currentValue - step;
      return nextValue >= 0
        ? nextValue
        : (childArray.length + (nextValue % childArray.length)) % childArray.length;
    });
  };

  return (
    <div className={`horizontal-scroll ${className}`.trim()} aria-label={ariaLabel}>
      <div className="horizontal-scroll-viewport">
        {showControls && (
          <>
            <button
              type="button"
              className="horizontal-scroll-arrow horizontal-scroll-arrow-left"
              onClick={handlePrevious}
              aria-label="Show previous items"
            >
              <svg className="horizontal-scroll-chevron" viewBox="0 0 20 20" aria-hidden="true">
                <path d="M12.5 3.5L6 10l6.5 6.5" />
              </svg>
            </button>
            <button
              type="button"
              className="horizontal-scroll-arrow horizontal-scroll-arrow-right"
              onClick={handleNext}
              aria-label="Show next items"
            >
              <svg className="horizontal-scroll-chevron" viewBox="0 0 20 20" aria-hidden="true">
                <path d="M7.5 3.5L14 10l-6.5 6.5" />
              </svg>
            </button>
          </>
        )}

        <div className={`horizontal-scroll-track ${trackClassName}`.trim()}>
          {visibleItems.map((entry) => (
            <div key={entry.key} className={`horizontal-scroll-item ${itemClassName}`.trim()}>
              {entry.child}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HorizontalScroll;